/**
 * Word-level diff for the Learn++ dashboard's note comparison.
 *
 * No diff dependency: the only caller is one admin panel, and the notes it
 * compares are a few thousand words at most.
 */

// Above this the quadratic table costs more than the highlight is worth, so
// the field renders as a plain replacement instead. A note section that long
// is a paste, not an edit.
const MAX_TOKENS = 2000;

const tokenize = (text) => String(text ?? '').match(/\s+|[^\s]+/g) || [];

const wordCount = (text) => tokenize(text).filter((t) => t.trim()).length;

// Flex++ structure markers, not note content.
const CONTROL_KEYS = new Set(['flex++', 'prune_early']);
const isPlainObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);
const contentKeys = (value) => Object.keys(value).filter((key) => !CONTROL_KEYS.has(key) && !key.startsWith('_'));
// A Flex++ field the AI left unanswered keeps its schema ({_description,
// _choices}); one already filled in the EMR carries {_existingValue}.
const isFlexLeaf = (value) => isPlainObject(value) && Object.keys(value).length > 0
  && Object.keys(value).every((key) => key.startsWith('_'));

/**
 * One note value as readable text: list rows (Raintree goals, exercises) become
 * `Field: value` lines separated by a blank line.
 */
export const noteValueText = (value) => {
  if (value == null) return '';
  if (Array.isArray(value)) {
    return value.map(noteValueText).filter(Boolean).join(value.some(isPlainObject) ? '\n\n' : '\n');
  }
  if (isFlexLeaf(value)) return noteValueText(value._existingValue);
  if (isPlainObject(value)) {
    return contentKeys(value).map((key) => {
      const text = noteValueText(value[key]);
      return text && `${key}: ${text}`;
    }).filter(Boolean).join('\n');
  }
  return String(value);
};

/**
 * Flatten a note's nested section objects into ordered `section / field` rows.
 */
export const flattenNote = (note) => {
  const rows = [];
  const walk = (value, path) => {
    if (isPlainObject(value) && !isFlexLeaf(value)) {
      contentKeys(value).forEach((key) => walk(value[key], path.concat(key)));
      return;
    }
    rows.push({
      section: path[0] || 'Note',
      field: path.length > 1 ? path.slice(1).join(' › ') : '',
      text: noteValueText(value),
    });
  };
  Object.keys(note || {}).forEach((section) => walk(note[section], [section]));
  return rows;
};

/**
 * Pair the AI note and the signed note into one ordered list of rows carrying
 * both sides. Field order follows Comprehend's note; anything only the
 * clinician has is appended after it, so a field they added still shows.
 */
export const pairNoteRows = (aiNote, finalNote) => {
  const rowKey = (r) => [r.section, r.field].join('\u0000');
  const ai = flattenNote(aiNote);
  const final = flattenNote(finalNote);
  const finalByKey = new Map(final.map((r) => [rowKey(r), r]));
  const seen = new Set();

  const rows = ai.map((r) => {
    seen.add(rowKey(r));
    const match = finalByKey.get(rowKey(r));
    return {
      section: r.section,
      field: r.field,
      before: r.text,
      after: match ? match.text : '',
    };
  });
  final.forEach((r) => {
    if (seen.has(rowKey(r))) return;
    rows.push({ section: r.section, field: r.field, before: '', after: r.text });
  });
  return rows;
};

/**
 * Segment two strings into `{ type: 'same' | 'removed' | 'added', text }`.
 * `removed` is what Comprehend wrote and the clinician took out, `added` is
 * what they put in its place.
 */
export const diffWords = (before, after) => {
  const a = tokenize(before);
  const b = tokenize(after);

  if (!a.length && !b.length) return [];
  if (a.join('') === b.join('')) return [{ type: 'same', text: a.join('') }];
  if (a.length > MAX_TOKENS || b.length > MAX_TOKENS) {
    const replaced = [];
    if (a.length) replaced.push({ type: 'removed', text: a.join('') });
    if (b.length) replaced.push({ type: 'added', text: b.join('') });
    return replaced;
  }

  // Longest common subsequence over tokens, walked back into segments.
  const lcs = Array.from(
    { length: a.length + 1 },
    () => new Uint32Array(b.length + 1),
  );
  for (let i = a.length - 1; i >= 0; i -= 1) {
    for (let j = b.length - 1; j >= 0; j -= 1) {
      lcs[i][j] =
        a[i] === b[j]
          ? lcs[i + 1][j + 1] + 1
          : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }

  const segments = [];
  const push = (type, text) => {
    const last = segments[segments.length - 1];
    if (last && last.type === type) last.text += text;
    else segments.push({ type, text });
  };

  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      push('same', a[i]);
      i += 1;
      j += 1;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      push('removed', a[i]);
      i += 1;
    } else {
      push('added', b[j]);
      j += 1;
    }
  }
  while (i < a.length) {
    push('removed', a[i]);
    i += 1;
  }
  while (j < b.length) {
    push('added', b[j]);
    j += 1;
  }
  return segments;
};

/**
 * How much of the note the clinician rewrote, as words added and words
 * removed over the length of Comprehend's original. Two numbers rather than
 * one because "cut half of it" and "doubled it" are different answers.
 */
export const changeSummary = (before, after) => {
  const segments = diffWords(before, after);
  const count = (type) =>
    segments.filter((s) => s.type === type).reduce((n, s) => n + wordCount(s.text), 0);
  const original = wordCount(before) || wordCount(after);
  const removed = count('removed');
  const added = count('added');
  return {
    words: original,
    removed,
    added,
    removedPct: original ? removed / original : 0,
    addedPct: original ? added / original : 0,
    unchanged: !removed && !added,
  };
};
